// src/pages/.../lessonPlan/TeacherWiseApprovalList.js
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import styles from "../../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import CustomMRTTable from "../../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../../services/Api";

const renderCellWithDash = ({ cell }) => {
  const value = cell.getValue();
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

// Try multiple shapes to find a numeric staff id on the API row.
// The staffWiseList response sometimes has it at the top level and
// sometimes only inside `details[].staff_id`.
const pickStaffId = (item) => {
  const candidates = [
    item?.staff_id,
    item?.id,
    item?.staffId,
    item?.teacher_id,
    ...(Array.isArray(item?.details)
      ? item.details.map((d) => d?.staff_id)
      : []),
  ];
  for (const c of candidates) {
    if (c != null && c !== "" && !Number.isNaN(Number(c))) {
      return Number(c);
    }
  }
  return null;
};

function TeacherWiseApprovalList() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const roleId = Number(user?.role?.id);
  const hideActions = [4, 6].includes(roleId);

  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_id = clientData?.academic_year_id || "";

  // ----------------------------------------------------------------
  // State
  // ----------------------------------------------------------------
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ----------------------------------------------------------------
  // Quick-lookup map: { filterId: value }
  // ----------------------------------------------------------------
  const filterMap = useMemo(() => {
    const map = {};
    columnFilters.forEach((f) => {
      if (f?.id !== undefined) map[f.id] = f.value;
    });
    return map;
  }, [columnFilters]);

  const getStr = useCallback(
    (id) => {
      const v = filterMap[id];
      if (v === undefined || v === null) return "";
      return String(v).trim();
    },
    [filterMap],
  );

  const staffNameFilter = getStr("staff_name");
  const subjectFilter = getStr("subjects");
  const genderFilter = getStr("gender");

  // ----------------------------------------------------------------
  // API — fetch teacher-wise lesson plan list
  // ----------------------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        school_id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        staff_name: staffNameFilter,
        subject: subjectFilter,
        gender: genderFilter,
        is_week: 1,
        academic_id: academic_id,
      };

      const response = await AxiosInstance.post(
        `/book/Hm-lesson-plans/staffWiseList`,
        payload,
      );

      if (response?.data?.status) {
        const rawList = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        const transformed = rawList.map((item, idx) => {
          const details = Array.isArray(item.details) ? item.details : [];

          const subjectsList = [
            ...new Set(
              details
                .map((d) => d.subject)
                .filter((s) => s && String(s).trim() !== ""),
            ),
          ];

          // Numeric staff_id — required by the view page APIs.
          const staff_id = pickStaffId(item);

          // Table row key — never sent to any API.
          const rowKey =
            staff_id != null
              ? `staff-${staff_id}`
              : `row-${item.staff_name || idx}`;

          // Normalize gender casing for display
          const rawGender = String(item.gender || "").trim();
          const displayGender = rawGender
            ? rawGender.charAt(0).toUpperCase() +
              rawGender.slice(1).toLowerCase()
            : "";

          return {
            id: rowKey,
            s_no: pagination.pageIndex * pagination.pageSize + idx + 1,
            staff_id,
            staff_name: item.staff_name || "",
            gender: displayGender,
            subjects: subjectsList.join(", "),
            details,
          };
        });

        // Client-side refinement on the joined "subjects" column.
        const localSubject = subjectFilter.toLowerCase();
        const finalData = localSubject
          ? transformed.filter((r) =>
              String(r.subjects).toLowerCase().includes(localSubject),
            )
          : transformed;

        setData(finalData);
        setRowCount(response.data.pagination?.total ?? finalData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (e) {
      console.error("Teacher-wise lesson plan fetch error:", e);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    school_id,
    pagination.pageIndex,
    pagination.pageSize,
    staffNameFilter,
    subjectFilter,
    genderFilter,
  ]);

  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [columnFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleColumnFiltersChange = (updaterOrValue) => {
    setColumnFilters((prev) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue,
    );
  };

  // ----------------------------------------------------------------
  // Navigate to the view page.
  // staff_id (numeric) MUST be present — it's used in every view API payload.
  // ----------------------------------------------------------------
  const handleView = useCallback(
    (row) => {
      if (row?.staff_id == null) {
        console.warn(
          "handleView: row has no staff_id, skipping navigation",
          row,
        );
        return;
      }
      navigate("/user/teacherWiseview", {
        state: {
          staff_id: row.staff_id,
          staff_name: row.staff_name,
          ID: row.id,
          data: row,
        },
      });
    },
    [navigate],
  );

  // ----------------------------------------------------------------
  // Columns
  // ----------------------------------------------------------------
  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: "staff_name",
        header: translations?.labels?.teachersName || "Teachers Name",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.teachersName || "Teacher Name",
        },
        Cell: renderCellWithDash,
      },
      {
        accessorKey: "gender",
        header: translations?.labels?.gender || "Gender",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "select",
        filterSelectOptions: [
          { text: "Male", value: "Male" },
          { text: "Female", value: "Female" },
          { text: "Other", value: "Other" },
        ],
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.gender || "Gender",
        },
        Cell: renderCellWithDash,
      },
      {
        accessorKey: "subjects",
        header: translations?.labels?.subjects || "Subjects",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: translations?.labels?.subjects || "Subjects",
        },
        Cell: renderCellWithDash,
      },
    ];

    if (!hideActions) {
      cols.push({
        id: "actions",
        header: translations?.labels?.action || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations?.labels?.view || "View"}
            disabled={row.original?.staff_id == null}
          >
            <FaEye />
          </button>
        ),
      });
    }

    return cols;
  }, [translations, hideActions, handleView]);

  return (
    <CustomMRTTable
      columns={columns}
      data={data}
      loading={loading}
      pagination={pagination}
      rowCount={rowCount}
      onPaginationChange={setPagination}
      columnFilters={columnFilters}
      onColumnFiltersChange={handleColumnFiltersChange}
      enableColumnFilters={true}
      enableGlobalFilter={false}
      hideGlobalSearch={true}
      enableExpanding={false}
    />
  );
}

export default TeacherWiseApprovalList;
